/**
 * Standardized Date and Time utilities.
 * Timezone is read dynamically from business settings (stored in localStorage at login).
 * Falls back to 'America/Chicago' if not set.
 */

const LOCALE = 'en-US';
const getTimezone = () => localStorage.getItem('etpos_business_timezone') || 'America/Chicago';
console.log('[DateUtils] Module Loaded');

/**
 * Returns a new Date object adjusted to Chicago time for display purposes.
 * Note: Actual Date objects are UTC-based, but this helps in getting 
 * "What day is it in Chicago right now?"
 */
export const getChicagoNow = () => {
  const now = new Date();
  // We use this to get the string representation in Chicago, then we could parse it
  // if we need a Date object that "looks" like Chicago time.
  return now;
};

/**
 * Formats a date to MM/DD/YYYY HH:MM AM/PM in business timezone.
 * For YYYY-MM-DD HH:mm:ss strings from the DB/API, parses directly
 * to avoid timezone-shifting when passing through new Date().
 */
export const formatDateTime = (date) => {
  if (!date) return '';
  if (typeof date === 'string') {
    // YYYY-MM-DD HH:mm:ss or YYYY-MM-DD HH:mm — parse directly, treat as business timezone.
    // Only match plain strings WITHOUT timezone indicators (no Z, no +offset, no .000000Z).
    // Strings with timezone info (e.g. created_at "2026-06-07T13:28:00.000000Z") fall through
    // to new Date() for proper UTC → business timezone conversion.
    const m = date.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
    if (m) {
      const [, yyyy, mo, dd, hh, min] = m;
      let h = parseInt(hh, 10);
      const ampm = h >= 12 ? 'PM' : 'AM';
      h = h % 12 || 12;
      return `${mo}/${dd}/${yyyy}, ${String(h).padStart(2, '0')}:${min} ${ampm}`;
    }
  }
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleString(LOCALE, {
    timeZone: getTimezone(),
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Formats a date to MM/DD/YYYY in business timezone.
 * For YYYY-MM-DD or YYYY-MM-DD HH:mm:ss strings, reads the date part directly
 * to avoid timezone-shifting when parsing with new Date().
 */
export const formatDate = (date) => {
  if (!date) return '';
  if (typeof date === 'string') {
    const m = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[2]}/${m[3]}/${m[1]}`;
  }
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString(LOCALE, {
    timeZone: getTimezone(),
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  });
};

/**
 * Formats a date to HH:MM AM/PM in Chicago timezone
 */
export const formatTime = (date) => {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleTimeString(LOCALE, {
    timeZone: getTimezone(),
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Returns "Today" in Chicago timezone as a local Date object (start of day).
 * This ensures that if it's currently May 8th in Chicago, we return a Date 
 * object representing May 8th 00:00:00 in the user's browser local time.
 */
export const getChicagoToday = () => {
  const chicagoStr = new Date().toLocaleString('en-US', { timeZone: getTimezone(), year: 'numeric', month: '2-digit', day: '2-digit' });
  const [mm, dd, yyyy] = chicagoStr.split('/');
  return new Date(yyyy, mm - 1, dd, 0, 0, 0, 0);
};

/**
 * Returns a YYYY-MM-DDTHH:mm string for the provided date.
 * Uses local getters to maintain the 'literal' date/time.
 */
export const getChicagoISOString = (date = new Date()) => {
  const d = typeof date === 'string' ? new Date(date) : date;
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: getTimezone(),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  
  const parts = formatter.formatToParts(d);
  const getPart = (type) => parts.find(p => p.type === type).value;
  const result = `${getPart('year')}-${getPart('month')}-${getPart('day')}T${getPart('hour')}:${getPart('minute')}`;
  console.log(`[DateUtils] getChicagoISOString for ${d.toISOString()} -> ${result}`);
  return result;
};

/**
 * Formats a date to YYYY-MM-DD HH:MM:SS for API compatibility.
 * Uses local getters to ensure the date/time being viewed by the user
 * is exactly what is sent to the API.
 */
export const formatDateForApi = (date = new Date()) => {
  if (!date) return '';
  const d = typeof date === 'string' 
    ? (date.includes('T') || date.includes(' ') ? new Date(date) : new Date(date.replace(/-/g, '/'))) 
    : date;

  
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  
  return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
};

/**
 * Formats a date to YYYY-MM-DD 23:59:59 for API compatibility (end of day).
 */
export const formatEndOfDateForApi = (date = new Date()) => {
  if (!date) return '';
  const d = typeof date === 'string' 
    ? (date.includes('T') || date.includes(' ') ? new Date(date) : new Date(date.replace(/-/g, '/'))) 
    : date;

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  
  return `${yyyy}-${mm}-${dd} 23:59:59`;
};

/**
 * Formats a date to YYYY-MM-DD HH:MM:SS in UTC for API compatibility.
 * Matches the live software's behavior of using toISOString().
 */
export const formatUTCForApi = (date = new Date()) => {
  if (!date) return '';
  
  if (typeof date === 'string') {
    // Case 1: YYYY-MM-DD — append current business timezone (Chicago) time
    if (date.length === 10 && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const now = new Date();
      const fmt = new Intl.DateTimeFormat('en-CA', {
        timeZone: getTimezone(),
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
      });
      const parts = fmt.formatToParts(now);
      const getPart = (type) => parts.find(p => p.type === type).value;
      return `${date} ${getPart('hour')}:${getPart('minute')}:${getPart('second')}`;
    }
    
    // Case 2: YYYY-MM-DDTHH:mm or YYYY-MM-DD HH:mm:ss
    if (date.match(/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2})?.*$/)) {
      let normalized = date.replace('T', ' ');
      if (normalized.length === 16) {
        normalized += ':00';
      }
      return normalized.substring(0, 19);
    }
    
    // Case 3: MM/DD/YYYY, HH:mm AM/PM or MM/DD/YYYY HH:mm:ss AM/PM
    const match = date.match(/^(\d{2})\/(\d{2})\/(\d{4})[,\s]+(\d{2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i);
    if (match) {
      const [, month, day, year, hour, minute, second = '00', ampm] = match;
      let hh = parseInt(hour, 10);
      if (ampm) {
        if (ampm.toUpperCase() === 'PM' && hh < 12) hh += 12;
        if (ampm.toUpperCase() === 'AM' && hh === 12) hh = 0;
      }
      const formattedHour = String(hh).padStart(2, '0');
      return `${year}-${month}-${day} ${formattedHour}:${minute}:${second}`;
    }
    
    // Fallback: parse string to Date
    const d = new Date(date);
    if (isNaN(d.getTime())) return date;
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }

  // If it's a Date object, format it in the business timezone
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: getTimezone(),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  const parts = formatter.formatToParts(date);
  const getPart = (type) => parts.find(p => p.type === type).value;
  return `${getPart('year')}-${getPart('month')}-${getPart('day')} ${getPart('hour')}:${getPart('minute')}:${getPart('second')}`;
};

/**
 * Returns the current time as YYYY-MM-DD HH:mm:ss in the business timezone (America/Chicago).
 * Use this when stamping new transactions so the timestamp matches the business setting,
 * regardless of the client's local system timezone.
 */
export const nowUTC = () => formatUTCForApi(new Date());

export default {
  formatDateTime,
  formatDate,
  formatTime,
  getChicagoNow,
  getChicagoToday,
  getChicagoISOString,
  formatDateForApi,
  formatEndOfDateForApi,
  formatUTCForApi,
  nowUTC
};
